// /settings/members container (memberships-settings.md, List archetype §4c):
// PageHeader → members table, with create in a right-anchored Drawer
// deep-linked via `?new=1`. Members is a team-BOUND settings screen
// (docs/09 §4): it consumes the shell's resolved working team as its list
// filter — the sidebar team selector is the one way to change it — and never
// mounts a screen-local team picker. `?agent` rides inert here.

import { useCallback, useEffect, useState } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { components } from "../api/schema";
import { apiClient } from "../lib/apiClient";
import { useCallerAuthority } from "../lib/authority";
import { ApiRequestError, requestErrorDescription } from "../lib/errors";
import { queryKeys } from "../lib/queryKeys";
import { useListQuery } from "../lib/useListQuery";
import {
  parseMembershipsSearch,
  type MembershipsSearch,
} from "../lib/routerSearch";
import {
  mergeSearch,
  useTypedNavigate,
  useTypedSearch,
} from "../lib/typedRouter";
import { useResolvedWorkingTeamId } from "../lib/workingScope";
import { Button, Page, PageHeader, useToast } from "../ui";
import { AddMemberDrawer } from "./memberships/AddMemberDrawer";
import {
  MembershipsTable,
  type MembershipPatchInput,
} from "./memberships/MembershipsTable";
import {
  isUuid,
  uniqueIds,
  userMap,
  useUserDirectoryQuery,
} from "./shared/userDirectory";

type MemberRole = components["schemas"]["MemberRole"];
type MembershipCreate = components["schemas"]["MembershipCreate"];

export function MembershipsSettings() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useTypedNavigate<MembershipsSearch>();
  const search = useTypedSearch(parseMembershipsSearch);

  // Drawer form state lives here (not in the Drawer) because the typed UUID
  // drives the `?ids=` resolution query below; it resets on every close.
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [appUserId, setAppUserId] = useState("");
  const [membershipRole, setMembershipRole] = useState<MemberRole>("member");

  // Affordance gate + ceiling come from membership-derived authority, never a
  // phantom `/v1/me` role claim.
  const authority = useCallerAuthority();

  // The resolved working team is the list filter; absent = the org-wide list
  // the API returns unfiltered.
  const workingTeamId = useResolvedWorkingTeamId();
  const isDrawerRequested = search.new === 1;
  // While authority is pending the drawer defers rendering and `?new` is
  // retained — stripping on pending would destroy every admin deep link.
  const isDrawerOpen = isDrawerRequested && authority.canManage;

  // The ONE search-param writer: a functional merge over the previous search
  // so sibling params survive, typed through the shared router helper.
  const patchSearch = useCallback(
    (patch: MembershipsSearch, replace = false): void => {
      void navigate({
        replace,
        search: mergeSearch<MembershipsSearch>(patch),
      });
    },
    [navigate],
  );

  useEffect(() => {
    if (isDrawerRequested && !authority.isPending && !authority.canManage) {
      patchSearch({ new: undefined }, true);
    }
  }, [
    isDrawerRequested,
    authority.isPending,
    authority.canManage,
    patchSearch,
  ]);

  function openDrawer(): void {
    patchSearch({ new: 1 });
  }

  // Split so the create mutation's onSuccess can close the drawer without a
  // circular type inference through `createMembership.reset()`; `closeDrawer`
  // (below, JSX-only) additionally clears a stale inline create error.
  function resetDrawerForm(): void {
    setSelectedTeamId("");
    setAppUserId("");
    setMembershipRole("member");
    patchSearch({ new: undefined }, true);
  }

  function closeDrawer(): void {
    createMembership.reset();
    resetDrawerForm();
  }

  const teams = useListQuery("/v1/teams", {
    queryKey: queryKeys.teams.list({ surface: "memberships-settings" }),
  });

  const membershipTeamId = workingTeamId;
  const memberships = useListQuery("/v1/memberships", {
    params: { team_id: membershipTeamId },
    queryKey: queryKeys.memberships.list({ team_id: membershipTeamId }),
  });

  const membershipUsers = useUserDirectoryQuery(
    uniqueIds(
      memberships.items.map((membership) => membership.app_user_id),
    ).join(","),
    "memberships-settings-members",
  );

  // Drawer-only visible-users directory for the datalist picker.
  const userOptions = useListQuery("/v1/users", {
    enabled: isDrawerOpen,
    queryKey: queryKeys.users.list({ surface: "memberships-settings-picker" }),
  });

  // Resolves the typed/pasted UUID to a visible user; disabled (empty ids)
  // until the input is a valid UUID.
  const normalizedAppUserId = appUserId.trim();
  const selectedUserQuery = useUserDirectoryQuery(
    isUuid(normalizedAppUserId) ? normalizedAppUserId : "",
    "memberships-settings-selected",
  );
  const selectedUser = selectedUserQuery.data?.items[0];

  // Async mutation outcomes surface as toasts (§4a); success invalidates the
  // memberships lists (this surface has no SSE stream; none added).
  function onMutationSuccess(message: string): () => Promise<void> {
    return async () => {
      toast.success(message);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.memberships.all,
      });
    };
  }

  // Typed `Error` so useMutation keeps its default TError (an `unknown`
  // callback would infer TError=unknown and untype `.error.message` reads).
  function onMutationError(title: string): (error: Error) => void {
    return (error) => {
      toast.error(title, {
        description: requestErrorDescription(
          error,
          "Membership request failed.",
        ),
      });
    };
  }

  const createMembership = useMutation({
    mutationFn: async (body: MembershipCreate) => {
      const { error } = await apiClient.POST("/v1/memberships", { body });
      if (error) {
        throw new ApiRequestError(error);
      }
    },
    onSuccess: async () => {
      resetDrawerForm();
      await onMutationSuccess("Member added")();
    },
    onError: onMutationError("Add member failed"),
  });

  const patchMembership = useMutation({
    mutationFn: async ({ id, member_role }: MembershipPatchInput) => {
      const { error } = await apiClient.PATCH("/v1/memberships/{id}", {
        body: { member_role },
        params: { path: { id } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
    },
    onSuccess: onMutationSuccess("Role updated"),
    onError: onMutationError("Role update failed"),
  });

  const deleteMembership = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await apiClient.DELETE("/v1/memberships/{id}", {
        params: { path: { id } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
    },
    onSuccess: onMutationSuccess("Member removed"),
    onError: onMutationError("Remove member failed"),
  });

  const teamsById: ReadonlyMap<string, string> = new Map(
    teams.items.map((team) => [team.id, team.display_name]),
  );
  const usersById = userMap([
    ...(membershipUsers.data?.items ?? []),
    ...userOptions.items,
    ...(selectedUser ? [selectedUser] : []),
  ]);

  return (
    <Page variant="workspace">
      {/* §4b.3: the action row stays fixed while the members table owns the
          remaining height and its own scroll, so "Add member" and the column
          headers never scroll away from a long roster. */}
      <PageHeader
        actions={
          authority.canManage ? (
            <Button size="sm" type="button" onClick={openDrawer}>
              Add member
            </Button>
          ) : undefined
        }
      />
      <MembershipsTable
        canManage={authority.canManage}
        deleteErrorMembershipId={
          deleteMembership.isError ? deleteMembership.variables : undefined
        }
        deleteErrorMessage={
          deleteMembership.isError ? deleteMembership.error.message : undefined
        }
        deletingMembershipId={
          deleteMembership.isPending ? deleteMembership.variables : undefined
        }
        directoryErrorMessage={
          membershipUsers.isError ? membershipUsers.error.message : undefined
        }
        grantableRoles={authority.grantableRoles}
        memberships={memberships}
        patchingMembershipId={
          patchMembership.isPending ? patchMembership.variables.id : undefined
        }
        teamsById={teamsById}
        usersById={usersById}
        onAddMember={openDrawer}
        onDeleteMembership={deleteMembership.mutateAsync}
        onPatchMembershipRole={(input) => {
          patchMembership.mutate(input);
        }}
        onResetDeleteMembership={deleteMembership.reset}
      />
      <AddMemberDrawer
        appUserId={appUserId}
        createErrorMessage={
          createMembership.isError ? createMembership.error.message : undefined
        }
        grantableRoles={authority.grantableRoles}
        isCreatePending={createMembership.isPending}
        isOpen={isDrawerOpen}
        membershipRole={membershipRole}
        pickerErrorMessage={
          userOptions.errorMessage ??
          (selectedUserQuery.isError
            ? selectedUserQuery.error.message
            : undefined)
        }
        selectedTeamId={selectedTeamId}
        selectedUser={selectedUser}
        teams={teams}
        userOptions={userOptions.items}
        onAppUserIdChange={setAppUserId}
        onDismiss={closeDrawer}
        onMembershipRoleChange={setMembershipRole}
        onSubmit={() => {
          createMembership.mutate({
            app_user_id: normalizedAppUserId,
            member_role: membershipRole,
            team_id: selectedTeamId,
          });
        }}
        onTeamIdChange={setSelectedTeamId}
      />
    </Page>
  );
}
